import { nowUtc } from '@/lib/clock';
import type { SaveState } from '@/features/save-status/save-status';

/** 文字輸入停止後多久才背景保存（spec 6.2）。 */
export const SAVE_DEBOUNCE_MS = 400;

export type SaveOperation = () => Promise<void>;

export interface SaveCoordinatorOptions {
  /** lastSavedAt 是最後一次成功寫入的 UTC 時間，沒寫過就是 null。 */
  onStateChange?: (state: SaveState, lastSavedAt: string | null) => void;
  debounceMs?: number;
}

function isQuotaError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED';
  }
  return false;
}

/**
 * 自動保存協調器（spec 6.2）。
 *
 * - 勾選、拖曳、選單與日期變更走 `run`，立即寫入。
 * - 文字輸入走 `schedule`，停止輸入 400ms 後才寫。
 * - 失焦、關閉抽屜、切換內容前呼叫 `flush`。
 * - 失敗時保留待寫入的內容，提供 `retry`，**絕不顯示假成功**。
 */
export function createSaveCoordinator(options: SaveCoordinatorOptions = {}) {
  const debounceMs = options.debounceMs ?? SAVE_DEBOUNCE_MS;

  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const pending = new Map<string, SaveOperation>();

  let state: SaveState = 'idle';
  let lastError: Error | null = null;
  let lastFailed: SaveOperation | null = null;
  /** 最後一次成功寫入的時間；狀態列要顯示「幾點幾分存的」，不然看不出是不是剛剛。 */
  let lastSavedAt: string | null = null;

  function setState(next: SaveState): void {
    state = next;
    options.onStateChange?.(next, lastSavedAt);
  }

  async function execute(operation: SaveOperation): Promise<void> {
    setState('saving');
    try {
      await operation();
      lastError = null;
      lastFailed = null;
      lastSavedAt = nowUtc();
      setState('saved');
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      lastFailed = operation;
      setState(isQuotaError(error) ? 'quota-exceeded' : 'error');
      throw error;
    }
  }

  /** 立即寫入。呼叫端仍會收到例外，方便回滾樂觀更新。 */
  async function run(operation: SaveOperation): Promise<void> {
    await execute(operation);
  }

  /** 依 key 分別 debounce，不同欄位不會互相取消。 */
  function schedule(key: string, operation: SaveOperation): void {
    const existing = timers.get(key);
    if (existing) clearTimeout(existing);

    pending.set(key, operation);
    timers.set(
      key,
      setTimeout(() => {
        // 失敗已經由 execute 記錄成 error 狀態，而且內容留在 pending 等 retry。
        // 這裡吞掉例外只是為了不要變成 unhandled rejection。
        void flush(key).catch(() => {});
      }, debounceMs),
    );
  }

  /** 立即寫出待保存內容；不傳 key 就全部寫出。 */
  async function flush(key?: string): Promise<void> {
    const keys = key ? [key] : [...pending.keys()];
    const entries: Array<{ key: string; operation: SaveOperation }> = [];

    for (const current of keys) {
      const timer = timers.get(current);
      if (timer) {
        clearTimeout(timer);
        timers.delete(current);
      }
      const operation = pending.get(current);
      if (operation) {
        pending.delete(current);
        entries.push({ key: current, operation });
      }
    }

    for (const [index, entry] of entries.entries()) {
      try {
        await execute(entry.operation);
      } catch (error) {
        // 失敗的那一筆與還沒輪到的那些都放回 pending，否則使用者剛打的字會無聲消失。
        // 期間若已經有更新的內容排進同一個 key，以新的為準，不要覆蓋回舊值。
        for (const remaining of entries.slice(index)) {
          if (!pending.has(remaining.key)) pending.set(remaining.key, remaining.operation);
        }
        // 內容已經回到 pending，改由 flush 負責重試，不再重複記在 lastFailed。
        lastFailed = null;
        throw error;
      }
    }
  }

  /**
   * 重試最近一次失敗的寫入。
   * `run` 的失敗留在 `lastFailed`，`flush` 的失敗留在 `pending`，兩邊都要補。
   */
  async function retry(): Promise<void> {
    const failed = lastFailed;
    lastFailed = null;
    if (failed) await execute(failed);
    if (pending.size > 0) await flush();
  }

  function hasPending(): boolean {
    return pending.size > 0;
  }

  function dispose(): void {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    pending.clear();
  }

  return {
    run,
    schedule,
    flush,
    retry,
    hasPending,
    dispose,
    getState: () => state,
    getLastError: () => lastError,
    getLastSavedAt: () => lastSavedAt,
  };
}

export type SaveCoordinator = ReturnType<typeof createSaveCoordinator>;

/**
 * 把「使用者要離開了」的時機接到 `flush`（spec 6.2 的缺口）。
 *
 * 原本 `flush` 只在失焦與關抽屜時呼叫，所以打字打到一半直接關分頁，
 * 最後 400ms 的內容會無聲消失。這裡補上兩個事件：
 *
 * - `visibilitychange`（切到別的分頁、最小化、手機切到背景）
 * - `pagehide`（真的要離開；Safari 的關閉不一定會先送 visibilitychange）
 *
 * 刻意**不掛 `beforeunload`**：它會讓分頁進不了 bfcache，而且無法等待非同步寫入，
 * 換來的保障沒有比 `pagehide` 多。
 */
export function attachLifecycleFlush(
  coordinator: Pick<SaveCoordinator, 'flush' | 'hasPending'>,
): () => void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return () => {};
  }

  const flushQuietly = (): void => {
    // 沒東西待寫就不要空跑，否則狀態列會在每次切分頁閃一下「儲存中」。
    if (!coordinator.hasPending()) return;
    // 失敗的內容已經由 flush 放回 pending 等重試，這裡吞掉例外只是避免 unhandled rejection。
    void coordinator.flush().catch(() => {});
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') flushQuietly();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', flushQuietly);

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', flushQuietly);
  };
}
